import React from 'react';
import { useLazyQuery, useMutation } from 'hooks/apollo';
import { Button, Col, Form, Input, Result, Row, Spin } from 'antd';
import { FormattedMessage } from 'react-intl';

import globalMessages from 'translations/messages';

import Page from 'components/Page';
import { EDIT_CUSTOMER } from 'shared/constants/permissions';
import useCustomer from 'hooks/useCustomer';
import { gql } from '@apollo/client';
import useUser from 'hooks/useUser';
import CountrySelect from 'components/CountrySelect';
import useGlobalContext from 'hooks/useGlobalContext';
import useFormatMessage from 'hooks/useFormatMessage';
import { PaymentType } from 'shared/prisma/types';
import { ExportOutlined } from '@ant-design/icons';
import SetupPaymentButton from 'components/SetupPaymentButton';
import messages from './messages';

const BILLING_DETAILS_MUTATION = gql`
  mutation CustomerBillingDetails(
    $id: ID!,
    $input: UpdateCustomerInput!
  ) {
    updateCustomer(
      id: $id,
      input: $input,
    ) {
      id
      billingAddress {
        id
        street
        city
        zip
        countryCode
      }
      billingComment
      vat
    }
  }
`;

const PAYMENT_TYPE_MUTATION = gql`
  mutation CustomerPaymentType(
    $id: ID!,
    $input: UpdateCustomerInput!
  ) {
    updateCustomer(
      id: $id,
      input: $input,
    ) {
      id
      paymentType
    }
  }
`;

const BILLING_PORTAL_QUERY = gql`
  query BillingPortal($customerId: ID!) {
    customer(id: $customerId) {
      id
      stripeCustomerId
      stripeBillingPortalUrl
    }
  }
`;

const BillingDetails = ({ customerId, onChangePaymentType }) => {
  const [form] = Form.useForm();
  const { message } = useGlobalContext();
  const $t = useFormatMessage();
  const [updateBilling, updateBillingRes] = useMutation(BILLING_DETAILS_MUTATION);
  const { customer, hasCustomerPermission, loading } = useCustomer({ customerId, extraFields: `
    billingAddress {
      id
      street
      city
      zip
      countryCode
    }
    billingComment
    vat    
  ` });

  if (loading) {
    return <Spin />;
  }

  const onFinish = async (values) => {
    try {
      await updateBilling({ variables: { id: customer.id, input: values } });
      message.success($t(messages.saveSuccess));
    } catch {
      message.error($t(messages.saveError));
    }
  };

  const canEdit = hasCustomerPermission(EDIT_CUSTOMER);

  const initialValues = {
    billingAddress: customer.billingAddress,
    billingComment: customer.billingComment,
    vat: customer.vat,
  };

  return (
    <Form layout="vertical" onFinish={onFinish} form={form} initialValues={initialValues}>
      <p>Please fill in your organizations billing information.</p>
      <Row gutter={16}>
        <Col span={11}>
          <Form.Item
            name={['billingAddress', 'street']}
            label="Street address"
          >
            <Input disabled={!canEdit} autoComplete="street-address" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name={['billingAddress', 'zip']}
                label="Postal code"
              >
                <Input disabled={!canEdit} autoComplete="postal-code" />
              </Form.Item>

            </Col>
            <Col span={12}>
              <Form.Item
                name={['billingAddress', 'city']}
                label="City"
              >
                <Input disabled={!canEdit} autoComplete="address-level2" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name={['billingAddress', 'countryCode']}
            label="Country"
          >
            <CountrySelect disabled={!canEdit} autoComplete="country" />
          </Form.Item>
        </Col>
        <Col offset={2} span={11}>
          <Form.Item
            name="vat"
            label="VAT number"
          >
            <Input disabled={!canEdit} />
          </Form.Item>
          <Form.Item
            name="billingComment"
            label="Extra information"
            help="Add any special information we need to know regarding billing and invoices here."
          >
            <Input.TextArea disabled={!canEdit} autoSize={{ minRows: 3 }} />
          </Form.Item>
        </Col>
      </Row>
      {canEdit && (
      <Page.Footer>
        <Button onClick={() => onChangePaymentType(null)}>
          Change payment method
        </Button>
        <Button type="primary" htmlType="submit" loading={updateBillingRes.loading}>
          <FormattedMessage {...globalMessages.save} />
        </Button>
      </Page.Footer>
      )}
    </Form>
  );
};

const CustomerBilling = ({ customerId }) => {
  const { customer, hasCustomerPermission, loading } = useCustomer({ customerId, extraFields: 'paymentType stripeCustomerId' });
  const [updatePaymentType, { loading: paymentTypeLoading }] = useMutation(PAYMENT_TYPE_MUTATION);
  const [getBillingPortalUrl, { loading: billingPortalUrlLoading }] = useLazyQuery(BILLING_PORTAL_QUERY);

  const { message } = useGlobalContext();
  const { isSuperUser } = useUser();

  if (loading) {
    return <Spin />;
  }

  if (!customer) {
    return (
      <div>Billing could not be loaded.</div>
    );
  }

  if (!hasCustomerPermission(EDIT_CUSTOMER)) {
    return (
      <div>You dont have permission to edit billing details, talk to the account owner.</div>
    );
  }

  const onChangePaymentType = async (paymentType) => {
    try {
      await updatePaymentType({ variables: { id: customer.id, input: { paymentType } } });
      message.success('Payment method updated');
    } catch {
      message.error('Could not update billing type');
    }
  };

  const onOpenBillingPortal = async () => {
    const { data } = await getBillingPortalUrl({ variables: { customerId: customer.id } });
    window.open(data.customer.stripeBillingPortalUrl, '_blank');
  };

  if (customer.paymentType === PaymentType.Invoice) {
    return (
      <BillingDetails customerId={customerId} onChangePaymentType={onChangePaymentType} />
    );
  }

  return (
    <Result
      icon={null}
      title="Billing"
      extra={(
        <>
          {customer.paymentType === PaymentType.Stripe ? (
            customer.stripeCustomerId && (
            <Button type="primary" onClick={onOpenBillingPortal} loading={billingPortalUrlLoading}>
              Open billing portal
              <ExportOutlined />
            </Button>
            )
          ) : (
            <SetupPaymentButton customerId={customerId} onSuccess={() => onChangePaymentType(PaymentType.Stripe)} />
          )}
          {isSuperUser && (
            <>
              {customer.paymentType && (
              <Button onClick={() => onChangePaymentType(null)} loading={paymentTypeLoading}>
                Remove payment method
              </Button>
              )}
              <Button onClick={() => onChangePaymentType(PaymentType.Invoice)} loading={paymentTypeLoading}>
                Setup invoice payment
              </Button>
            </>
          )}
        </>
      )}
    />
  );
};

export default CustomerBilling;
